import { nextTick, ref, toRaw } from "vue";
import { useColorStore } from "@/classes/useColorStore.js";
import { usePageStore } from "@/stores/pageStore";
import { isTheme } from "@/themes/registry.js";
import { cleanLook, paletteNotes } from "@/themes/look.js";
import { createStudioOverlay } from "@/classes/studioOverlay.js";
import { collectPexels, normalisePath, parseStudioMessage } from "@/utils/studioLogic.js";

// The preview build, framed by tokyo-ui, talks to its parent window.
//
// Website setup (PreviewSite.vue), unchanged:
//   parent -> here  {type: 'draksy-preview-setup', setup: true}
//                   hides the Edit button (Tokyo shows its own Setup menu)
//   parent -> here  {type: 'draksy-preview-look', theme, brandColors, look}
//                   re-skins the page live, before anything is saved
//   here -> parent  {type: 'draksy-preview-ready'} once mounted, and
//                   {type: 'draksy-preview-location', path} on every page change
//                   {type: 'draksy-preview-palette', notes: [{colour, use, text}]} after every
//                   look message: the colours the palette had to darken or lighten so
//                   text stays readable (empty when none), shown as a quiet note in Look
//
// Website Studio (WEBSITE_BUILD.md, contract 1):
//   parent -> here  draksy-studio-mode {mode: 'setup'|'edit'|'off'}
//                   draksy-studio-select {id|null, scroll, label}
//                   draksy-studio-spotlight {id|null}
//                   draksy-studio-page {path, page: {title, description, sections}}
//                   draksy-studio-insert-points {show}
//   here -> parent  draksy-studio-ready {path}
//                   draksy-studio-sections {path, sections: [{id, type, top, height}], viewport: {height, scrollY}}
//                   draksy-studio-section-click {id}
//                   draksy-studio-insert-click {index}
//
// Only messages from the framing window are read. Looks only accept known
// theme names and #hex colours; studio messages are validated and copied by
// parseStudioMessage, and draft words are rendered through the components'
// usual escaping and sanitising. Nothing here saves anything: that's Tokyo's job.

export const previewSetupMode = ref(false);
// 'setup' | 'edit' | 'off'. Anything but 'off' hides naples' own Edit button.
export const studioMode = ref("off");

const HEX = /^#[0-9a-f]{3,8}$/i;
let lookOverride = null;

const cleanColours = (colours) => {
    if (!colours || typeof colours !== "object") return null;
    const out = {};
    for (const [key, value] of Object.entries(colours)) {
        if (/^[a-zA-Z]{1,32}$/.test(key) && typeof value === "string" && HEX.test(value)) {
            out[key] = value;
        }
    }
    return Object.keys(out).length > 0 ? out : null;
};

const applyLook = (pinia) => {
    if (!lookOverride) return;
    // The "Make it yours" choices ride along with theme and colours; a new
    // message replaces the old (Reset sends {backgrounds: "tinted"}).
    const pd = usePageStore(pinia).pageData;
    if (lookOverride.look && isPageObject(pd)) {
        usePageStore(pinia).pageData = { ...pd, look: lookOverride.look };
    }
    const colorStore = useColorStore(pinia);
    const pageStore = usePageStore(pinia);
    if (lookOverride.theme) {
        colorStore.setTheme(lookOverride.theme);
    }
    if (lookOverride.brandColors) {
        const merged = { ...(pageStore.pageData?.brandColors || {}), ...lookOverride.brandColors };
        colorStore.setColors(merged);
        if (pageStore.pageData && typeof pageStore.pageData === "object" && !Array.isArray(pageStore.pageData)) {
            pageStore.pageData = { ...pageStore.pageData, brandColors: merged, theme: lookOverride.theme || pageStore.pageData.theme };
        }
    }
    reportPalette?.(colorStore.theme, { ...(pageStore.pageData?.brandColors || {}), ...(lookOverride.brandColors || {}) }, lookOverride.look);
};
// Set once the bridge is installed (it needs the parent window).
let reportPalette = null;

const SHOP_TYPES = new Set(["product-list", "category-list", "featured-picks", "collection-spotlight", "live-price", "see-in-shop"]);
export const draftHasShopSections = (sections) => Array.isArray(sections) && sections.some((x) => SHOP_TYPES.has(x?.type));

const isPageObject = (pd) => !!pd && typeof pd === "object" && !Array.isArray(pd);

export function installPreviewBridge(router, pinia) {
    if (import.meta.env.VITE_IS_PREVIEW_SERVER !== "true") return;
    if (typeof window === "undefined" || window.self === window.top) return;

    const tell = (message) => window.parent.postMessage(message, "*");
    reportPalette = (themeName, brand, look) => tell({ type: "draksy-preview-palette", notes: paletteNotes(themeName, brand, look) });
    const pageStore = usePageStore(pinia);
    const currentPath = () => normalisePath(router.currentRoute.value.path) || "/";

    // ---------------------------------------------------------- studio

    const overlay = createStudioOverlay({
        post: tell,
        getPath: currentPath,
        getTotalSections: () => (Array.isArray(pageStore.pageData?.sections) ? pageStore.pageData.sections.length : 0),
    });

    // Live drafts by path. The latest one for the page on show replaces its
    // sections; it is put back whenever the page data is fetched again (a
    // page change, or Tokyo's draft arriving before the fetch lands).
    const drafts = new Map();
    let appliedSections = null;
    let appliedFrom = null; // the draft's own sections array that appliedSections was made from
    let selfUpdate = false;

    const applyDraft = () => {
        const draft = drafts.get(currentPath());
        const pd = pageStore.pageData;
        // The store hands back reactive proxies: compare the raw arrays.
        if (!draft || !isPageObject(pd) || toRaw(pd.sections) === draft.sections) return;
        if (toRaw(pd.sections) === appliedSections && appliedFrom === draft.sections) return;
        // The contact details section's phone numbers, emails and addresses are added by the server (contactPage.js),
        // not sent by Tokyo: keep them from the page on show, and pick them with the draft's own selectedIds.
        const served = (pd.sections || []).find((x) => x?.type === 'contact-details' && (x.phone || x.email || x.address));
        const sections = served
            ? draft.sections.map((x) => (x?.type === 'contact-details'
                ? {...x, phone: served.phone, email: served.email, address: served.address, includedIds: Array.isArray(x.selectedIds) ? x.selectedIds : []}
                : x))
            : draft.sections;
        appliedSections = sections;
        appliedFrom = draft.sections;
        // The header, footer and call bar show the first ticked detail of each kind, live as they're ticked.
        const ticked = served ? draft.sections.find((x) => x?.type === 'contact-details') : null;
        const first = (list) => (Array.isArray(list) ? list.find((r) => (ticked?.selectedIds || []).includes(r?.id)) : undefined) || undefined;
        const siteWide = ticked ? {businessPhone: first(served.phone), businessEmail: first(served.email), businessAddress: first(served.address)} : {};
        selfUpdate = true;
        pageStore.pageData = {
            ...pd,
            ...siteWide,
            sections,
            // Home has no page title (null); don't invent one.
            pageTitle: pd.pageTitle && draft.title ? draft.title : pd.pageTitle,
            pexels: collectPexels(draft.sections),
        };
        selfUpdate = false;
        nextTick(() => overlay.refresh());
    };

    // The shop's sections in a draft carry only their settings (which
    // category, which picks); their products are filled in by the preview
    // server, then the draft is shown again - unless a newer one came in.
    const fillShopSections = async (path, page) => {
        if (!draftHasShopSections(page.sections)) return;
        const asked = page.sections.filter((x) => SHOP_TYPES.has(x?.type)).slice(0, 20);
        try {
            const bytes = new TextEncoder().encode(JSON.stringify(asked));
            let bin = "";
            bytes.forEach((b) => { bin += String.fromCharCode(b); });
            const s = btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
            const r = await fetch(`/api/preview/api/shop-sections?s=${s}`, {credentials: "same-origin"});
            if (!r.ok) return;
            const {sections} = await r.json();
            if (drafts.get(path) !== page || !Array.isArray(sections)) return;
            const byId = new Map(sections.filter((x) => x?.id).map((x) => [x.id, x]));
            drafts.set(path, {...page, sections: page.sections.map((x) => (x?.id && byId.has(x.id) ? byId.get(x.id) : x))});
            if (path === currentPath()) applyDraft();
        } catch (e) { /* the section shows its studio placeholder instead */ }
    };

    // draksy-studio-ready goes once the page's own data is in (so a draft or
    // selection Tokyo sends in reply lands on the right page), with a fallback.
    let awaitingReady = null; // {path, pageData, timer}
    const sendReady = () => {
        if (awaitingReady) clearTimeout(awaitingReady.timer);
        awaitingReady = null;
        nextTick(() => {
            tell({ type: "draksy-studio-ready", path: currentPath() });
            overlay.refresh();
        });
    };

    window.addEventListener("message", (event) => {
        if (event.source !== window.parent) return;
        const data = event.data;
        if (!data || typeof data !== "object") return;
        if (data.type === "draksy-preview-setup") {
            previewSetupMode.value = data.setup === true;
            return;
        }
        if (data.type === "draksy-preview-look") {
            const theme = typeof data.theme === "string" && isTheme(data.theme) ? data.theme : null;
            lookOverride = { theme, brandColors: cleanColours(data.brandColors), look: cleanLook(data.look) };
            applyLook(pinia);
            overlay.refresh();
            return;
        }
        const msg = parseStudioMessage(data);
        if (!msg) return;
        switch (msg.type) {
            case "draksy-studio-mode":
                studioMode.value = msg.mode;
                overlay.setMode(msg.mode);
                break;
            case "draksy-studio-select":
                overlay.select(msg.id, { scroll: msg.scroll, label: msg.label });
                break;
            case "draksy-studio-spotlight":
                overlay.spotlight(msg.id);
                break;
            case "draksy-studio-insert-points":
                overlay.showInsertPoints(msg.show);
                break;
            case "draksy-studio-page":
                if (msg.page === null) drafts.delete(msg.path);
                else drafts.set(msg.path, msg.page);
                if (msg.path === currentPath()) applyDraft();
                if (msg.page) fillShopSections(msg.path, msg.page);
                break;
            default:
                break;
        }
    });

    // A page change refetches the page data with the saved look; put the
    // unsaved one back on top.
    pageStore.$subscribe(() => {
        const shown = pageStore.pageData?.brandColors;
        if (lookOverride?.brandColors && shown
            && Object.entries(lookOverride.brandColors).some(([key, value]) => shown[key] !== value)) {
            applyLook(pinia);
        }
        if (lookOverride?.look && isPageObject(pageStore.pageData)
            && JSON.stringify(pageStore.pageData.look || null) !== JSON.stringify(lookOverride.look)) {
            applyLook(pinia);
        }
        if (selfUpdate) return;
        // Fresh page data from the server: put the live draft back on top.
        if (isPageObject(pageStore.pageData) && toRaw(pageStore.pageData.sections) !== appliedSections) {
            applyDraft();
        }
        if (awaitingReady && toRaw(pageStore.pageData) !== awaitingReady.pageData
            && awaitingReady.path === currentPath()) {
            sendReady();
        }
    });
    const colorStore = useColorStore(pinia);
    colorStore.$subscribe(() => {
        if (lookOverride?.theme && colorStore.theme !== lookOverride.theme) {
            colorStore.setTheme(lookOverride.theme);
        }
    });

    router.afterEach((to, from) => {
        tell({ type: "draksy-preview-location", path: to.path });
        if (normalisePath(to.path) === normalisePath(from?.path)) return;
        if (awaitingReady) clearTimeout(awaitingReady.timer);
        awaitingReady = {
            path: normalisePath(to.path),
            pageData: toRaw(pageStore.pageData),
            timer: setTimeout(sendReady, 4000),
        };
        overlay.refresh();
    });

    overlay.start();
    tell({ type: "draksy-preview-location", path: router.currentRoute.value.path });
    tell({ type: "draksy-preview-ready" });
    sendReady();
}
